import { upperFirst } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { Options as RRuleOptions } from 'rrule';
import { Menu, useMenuContext } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { OffsetOptionsMobile } from '@shared/scenes/entity/components/fields/DateEditField/OffsetOptionsMobile';
import { useBatchSchedule } from '@shared/scenes/entity/components/fields/DateEditField/hooks/useBatchSchedule';
import { useIsNeverEnabled } from '@shared/scenes/entity/components/fields/DateEditField/hooks/useIsNeverEnabled';
import { Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { PM60, useFeatureFlag } from '@shared/util/featureFlags';
import { i18n } from '@shared/util/i18n';
import { AutoExtendOptionsMobile } from './AutoExtendOptionsMobile';
import { CustomBatch } from './CustomBatch';
import { EndDatePickerCalendar } from './EndDatePickerCalendar';
import { AutoExtendConditionType, BatchScheduleComponentProps, RepeatEndType } from './types';
import { MAX_COUNT, getAllDates, getInitialAutoExtendConditionType, getRruleText } from './utils';

export function BatchScheduleMobile({
  date,
  rruleOptions,
  setRruleOptions,
  showingCustomOptions,
  setShowingCustomOptions,
  showingTagFilterOptions,
  setShowingTagFilterOptions,
  presets,
  count,
  setCount,
  until,
  setUntil,
  repeatEndType,
  setRepeatEndType,
  presetIndexSelected,
  setPresetIndexSelected,
  isCustomSelected,
  setUserIsEditing,
  minimumInSeries,
  setMinimumInSeries,
  tagFilter,
  setTagFilter,
  component,
  workflowTemplateId,
  offset,
  setOffset,
  setRecurrenceType,
  recurrenceType,
}: BatchScheduleComponentProps) {
  const { handleCountChange, handlePresetSelect, handleCustomSelect, handleOffsetSelect } =
    useBatchSchedule({
      date,
      count,
      setCount,
      setPresetIndexSelected,
      setRruleOptions,
      presets,
      setShowingCustomOptions,
      setUserIsEditing,
      setOffset,
      setRecurrenceType,
    });

  const [conditionType, setConditionType] = useState<AutoExtendConditionType>(
    getInitialAutoExtendConditionType({
      minimumInSeries,
      tagFilter,
      component,
    }),
  );

  // Track the previous `conditionType` to ensure we only react to actual changes.
  const prevConditionTypeRef = useRef<AutoExtendConditionType>(conditionType);

  useEffect(() => {
    // Bail early if `conditionType` hasn't actually changed.
    if (prevConditionTypeRef.current === conditionType) {
      return;
    }

    prevConditionTypeRef.current = conditionType;

    if (conditionType === 'default') {
      setMinimumInSeries(component?.autoExtendDefault?.minimumInSeries ?? MAX_COUNT);
      setTagFilter({
        componentId: component?.autoExtendDefault?.impliedFilterComponentId ?? '',
        value: {
          containsString: null,
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      });
    } else if (conditionType === 'custom') {
      setMinimumInSeries(MAX_COUNT);
      setTagFilter(undefined);
    }
  }, [conditionType, minimumInSeries, tagFilter, component, setMinimumInSeries, setTagFilter]);

  const handleUntilChange = useCallback(
    (newUntil: Date) => {
      setUntil?.(newUntil);
      setUserIsEditing(true);
    },
    [setUntil, setUserIsEditing],
  );

  const isPM60Enabled = useFeatureFlag(PM60);
  const showPresetOptions = !showingCustomOptions && !showingTagFilterOptions;
  const showEndMenu =
    recurrenceType === 'RRULE_SCHEDULE' && !showingCustomOptions && !showingTagFilterOptions;
  const showAutoExtendOptions =
    recurrenceType === 'RRULE_SCHEDULE' && isPM60Enabled && !showingCustomOptions;
  const showOffsetOptions = recurrenceType === 'OFFSET';

  return (
    <View paddingHorizontal={2} paddingVertical={1}>
      {showPresetOptions && (
        <PresetOptions
          presets={presets}
          recurrenceType={recurrenceType}
          presetIndexSelected={presetIndexSelected}
          isCustomSelected={isCustomSelected}
          handlePresetSelect={handlePresetSelect}
          handleCustomSelect={handleCustomSelect}
          handleOffsetSelect={handleOffsetSelect}
        />
      )}
      {showOffsetOptions && (
        <OffsetOptionsMobile
          offset={offset}
          setOffset={setOffset}
          tagFilter={tagFilter}
          setTagFilter={setTagFilter}
          workflowTemplateId={workflowTemplateId}
        />
      )}
      {showEndMenu && (
        <EndMenu
          repeatEndType={repeatEndType}
          count={count}
          handleCountChange={handleCountChange}
          until={until}
          onUntilSelected={handleUntilChange}
          rruleOptions={rruleOptions}
          setRepeatEndType={setRepeatEndType}
          minimumInSeries={minimumInSeries}
          tagFilter={tagFilter}
        />
      )}
      {showAutoExtendOptions && (
        <AutoExtendOptionsMobile
          minimumInSeries={minimumInSeries}
          setMinimumInSeries={setMinimumInSeries}
          tagFilter={tagFilter}
          setTagFilter={setTagFilter}
          component={component}
          workflowTemplateId={workflowTemplateId}
          showingTagFilterOptions={showingTagFilterOptions}
          setShowingTagFilterOptions={setShowingTagFilterOptions}
          conditionType={conditionType}
          setConditionType={setConditionType}
          setRepeatEndType={setRepeatEndType}
        />
      )}
      {showingCustomOptions && (
        <CustomBatch
          rruleOptions={rruleOptions}
          setRruleOptions={setRruleOptions}
          setUserIsEditing={setUserIsEditing}
        />
      )}
    </View>
  );
}

function PresetOptions({
  presets,
  presetIndexSelected,
  isCustomSelected,
  recurrenceType,
  handlePresetSelect,
  handleCustomSelect,
  handleOffsetSelect,
}: {
  presets: Partial<RRuleOptions>[];
  presetIndexSelected: number;
  isCustomSelected: boolean;
  recurrenceType: API.RecurrenceType | undefined;
  handlePresetSelect: (preset: Partial<RRuleOptions>, index: number) => void;
  handleCustomSelect: VoidFunction;
  handleOffsetSelect: VoidFunction;
}) {
  return (
    <>
      <Text size="body1" weight="semibold">
        {i18n('workflowTemplate.component.dateAndRepeat.repeat')}
      </Text>
      <TouchableOpacity
        key="offset"
        onPress={handleOffsetSelect}
        style={{
          paddingTop: grid(1),
          paddingBottom: grid(1),
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexShrink: 0,
          flexGrow: 0,
          width: '100%',
        }}
      >
        <View style={{ flexBasis: grid(3) }} marginRight={4}>
          {recurrenceType === 'OFFSET' && (
            <>
              <MBIconV2 color={Colors.v2.green} name="check-filled" size={grid(5)} />
            </>
          )}
          {recurrenceType !== 'OFFSET' && (
            <MBIconV2 color={Colors.v2.green} name="unfilled-circle" size={grid(5)} />
          )}
        </View>
        <View flex>
          <Text size="body1">
            {i18n('workflowTemplate.component.dateAndRepeat.offset.oneAtATimeTitle')}
          </Text>
        </View>
      </TouchableOpacity>
      {presets.map((preset, index) => {
        const presetWithoutCount = { ...preset, count: undefined, until: undefined };
        const rruleText = getRruleText(presetWithoutCount);

        return (
          <TouchableOpacity
            key={rruleText}
            onPress={() => handlePresetSelect(preset, index)}
            style={{
              paddingTop: grid(1),
              paddingBottom: grid(1),
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexShrink: 0,
              flexGrow: 0,
              width: '100%',
            }}
          >
            <View style={{ flexBasis: grid(3) }} marginRight={4}>
              {presetIndexSelected === index && !isCustomSelected && (
                <>
                  {presets.length === 1 && (
                    <MBIconV2 color={Colors.v2.green} name="check-filled" size={grid(5)} />
                  )}
                  {presets.length > 1 && (
                    <MBIconV2 color={Colors.v2.green} name="radio-button" size={grid(5)} />
                  )}
                </>
              )}
              {(presetIndexSelected !== index || isCustomSelected) && (
                <MBIconV2 color={Colors.v2.green} name="unfilled-circle" size={grid(5)} />
              )}
            </View>
            <View flex>
              <Text size="body1">{upperFirst(rruleText)}</Text>
            </View>
          </TouchableOpacity>
        );
      })}
      <TouchableOpacity
        key="custom"
        onPress={handleCustomSelect}
        style={{
          paddingTop: grid(1),
          paddingBottom: grid(1),
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexShrink: 1,
          flexGrow: 0,
          width: '100%',
        }}
      >
        <View style={{ flexBasis: grid(3) }} marginRight={4}>
          {isCustomSelected ? (
            <MBIconV2 color={Colors.v2.green} name="radio-button" size={grid(5)} />
          ) : (
            <MBIconV2 color={Colors.v2.green} name="unfilled-circle" size={grid(5)} />
          )}
        </View>
        <View row centerVertically flex style={{ justifyContent: 'space-between' }}>
          <Text size="body1">{i18n('workflowTemplate.component.dateAndRepeat.custom')}</Text>
          <MBIconV2 color={Colors.v2.purple} name="arrow-point-right" size={grid(3.5)} />
        </View>
      </TouchableOpacity>
    </>
  );
}

function getRepeatEndTypeLabel(type: RepeatEndType): string {
  switch (type) {
    case RepeatEndType.COUNT:
      return i18n('workflowTemplate.component.dateAndRepeat.after');
    case RepeatEndType.END_DATE:
      return i18n('workflowTemplate.component.dateAndRepeat.on');
    case RepeatEndType.NEVER:
      return i18n('workflowTemplate.component.dateAndRepeat.never');
    default:
      return '';
  }
}

function UntilDateMenuCalendar({
  until,
  rruleOptions,
  onUntilSelected,
}: {
  until?: Date;
  rruleOptions?: Partial<RRuleOptions>;
  onUntilSelected: (newUntil: Date) => void;
}) {
  const { close } = useMenuContext();
  const fallbackDate = useMemo(() => {
    const all = getAllDates({ rruleOptions });
    return all.length ? all[all.length - 1] : new Date();
  }, [rruleOptions]);

  const selectedDate = until ?? fallbackDate;

  const handleSelect = (date: Date) => {
    const dtstartDate = moment(rruleOptions?.dtstart);
    const dtstartHour = dtstartDate.hour();
    const dtstartMinute = dtstartDate.minute();
    const dtstartSecond = dtstartDate.second();
    const dtstartMillisecond = dtstartDate.millisecond();

    const untilDate = moment(date);
    untilDate.hour(dtstartHour);
    untilDate.minute(dtstartMinute);
    untilDate.second(dtstartSecond);
    untilDate.millisecond(dtstartMillisecond);

    onUntilSelected(untilDate.toDate());
    close();
  };

  return (
    <EndDatePickerCalendar
      selectedDate={selectedDate}
      minDate={rruleOptions?.dtstart as Date | undefined}
      rruleOptions={rruleOptions}
      onSelect={handleSelect}
    />
  );
}

interface EndMenuProps {
  repeatEndType: RepeatEndType;
  count?: number;
  handleCountChange: (newCount: string) => void;
  until?: Date;
  onUntilSelected: (newUntil: Date) => void;
  rruleOptions?: Partial<RRuleOptions>;
  setRepeatEndType: (newRepeatEndType: RepeatEndType) => void;
  minimumInSeries?: number;
  tagFilter?: API.FilterByComponentFragment;
}

function EndMenu({
  repeatEndType,
  count,
  handleCountChange,
  until,
  onUntilSelected,
  rruleOptions,
  setRepeatEndType,
  minimumInSeries,
  tagFilter,
}: EndMenuProps) {
  const isNeverEndTypeEnabled = useIsNeverEnabled({ minimumInSeries, tagFilter });

  const repeatEndTypeOptions = useMemo(() => {
    const opts = [RepeatEndType.COUNT, RepeatEndType.END_DATE];
    if (isNeverEndTypeEnabled) {
      opts.push(RepeatEndType.NEVER);
    }
    return opts;
  }, [isNeverEndTypeEnabled]);

  const untilDisplayDate = useMemo(() => {
    const all = getAllDates({ rruleOptions });
    const fallback = all.length ? all[all.length - 1] : new Date();
    return until ?? fallback;
  }, [until, rruleOptions]);

  return (
    <View
      row
      centerVertically
      gap={1}
      marginVertical={2.5}
      style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}
    >
      <Text size="body1" weight="semibold">
        {i18n('workflowTemplate.component.dateAndRepeat.ends')}
      </Text>
      <Menu align="center" closeOnSelect>
        <Menu.Button label={getRepeatEndTypeLabel(repeatEndType)} style={{ flexShrink: 0 }} />
        <Menu.Content>
          <Menu.Options
            options={repeatEndTypeOptions}
            getItemKey={option => option}
            getItemLabel={getRepeatEndTypeLabel}
            isItemSelected={option => option === repeatEndType}
            onSelect={setRepeatEndType}
          />
        </Menu.Content>
      </Menu>
      {repeatEndType === RepeatEndType.COUNT && (
        <>
          <MBTextInput
            value={String(count ?? '')}
            onChangeText={handleCountChange}
            noPadding
            style={{
              width: grid(8),
              height: grid(5.5),
              paddingHorizontal: grid(2),
              backgroundColor: Colors.v2.backgroundGrayLight,
              borderWidth: 0,
            }}
          />
          <Text size="body1">{i18n('workflowTemplate.component.dateAndRepeat.times')}</Text>
        </>
      )}
      {repeatEndType === RepeatEndType.END_DATE && (
        <Menu closeOnSelect={false} align="center">
          <Menu.Button label={moment(untilDisplayDate).format('MMM D, YYYY')} flex />
          <Menu.Content style={{ minWidth: grid(36) }}>
            <UntilDateMenuCalendar
              until={until}
              rruleOptions={rruleOptions}
              onUntilSelected={onUntilSelected}
            />
          </Menu.Content>
        </Menu>
      )}
    </View>
  );
}
